import { graphql } from "gatsby"

export type LuasUiLabels = {
  line: string
  stop: string
  selectStop: string
  find: string
  loadingForecasts: string
  loadingTravelUpdates: string
  stopsUnavailable: string
  forecastUnavailable: string
  travelUpdatesUnavailable: string
  destination: string
  minutes: string
  noTramsScheduled: string
  goodService: string
  disrupted: string
  normalServiceMessage: string
  selectLine: string
  stopInformation: string
  travelUpdates: string
  latestUpdate: string
  forecastLoadedAnnouncement: string
  servicesSuffix: string
}

export type LuasUiLabelsRecord = Partial<{
  lineLabel: string | null
  stopLabel: string | null
  selectLineLabel: string | null
  selectStopLabel: string | null
  findLabel: string | null
  loadingForecastsLabel: string | null
  loadingTravelUpdatesLabel: string | null
  stopsUnavailableLabel: string | null
  forecastUnavailableLabel: string | null
  travelUpdatesUnavailableLabel: string | null
  destinationLabel: string | null
  minutesLabel: string | null
  noTramsScheduledLabel: string | null
  normalServiceLabel: string | null
  disruptedLabel: string | null
  stopInformationLabel: string | null
  travelUpdatesLabel: string | null
  latestUpdateLabel: string | null
  forecastLoadedAnnouncement: string | null
  servicesSuffix: string | null
}>

const englishLabels: LuasUiLabels = {
  line: "Line",
  stop: "Stop",
  selectStop: "- Select a stop -",
  find: "Find",
  loadingForecasts: "Loading forecasts...",
  loadingTravelUpdates: "Loading travel updates...",
  stopsUnavailable: "Could not load stops list. Please try again later.",
  forecastUnavailable: "Could not load forecast. Please try again.",
  travelUpdatesUnavailable: "Could not load travel updates. Please try again later.",
  destination: "Destination",
  minutes: "Mins",
  noTramsScheduled: "No trams scheduled",
  goodService: "Operating normally",
  disrupted: "Disrupted",
  normalServiceMessage: "Operating normally",
  selectLine: "- Select a line -",
  stopInformation: "Luas stop information",
  travelUpdates: "Travel updates",
  latestUpdate: "Latest update",
  forecastLoadedAnnouncement: "Forecast loaded for {stop}. {directions} directions available.",
  servicesSuffix: "services",
}

export function getLuasUiLabels(record?: LuasUiLabelsRecord | null): LuasUiLabels {
  return {
    line: record?.lineLabel || englishLabels.line,
    stop: record?.stopLabel || englishLabels.stop,
    selectLine: record?.selectLineLabel || englishLabels.selectLine,
    selectStop: record?.selectStopLabel || englishLabels.selectStop,
    find: record?.findLabel || englishLabels.find,
    loadingForecasts: record?.loadingForecastsLabel || englishLabels.loadingForecasts,
    loadingTravelUpdates: record?.loadingTravelUpdatesLabel || englishLabels.loadingTravelUpdates,
    stopsUnavailable: record?.stopsUnavailableLabel || englishLabels.stopsUnavailable,
    forecastUnavailable: record?.forecastUnavailableLabel || englishLabels.forecastUnavailable,
    travelUpdatesUnavailable: record?.travelUpdatesUnavailableLabel || englishLabels.travelUpdatesUnavailable,
    destination: record?.destinationLabel || englishLabels.destination,
    minutes: record?.minutesLabel || englishLabels.minutes,
    noTramsScheduled: record?.noTramsScheduledLabel || englishLabels.noTramsScheduled,
    goodService: record?.normalServiceLabel || englishLabels.goodService,
    disrupted: record?.disruptedLabel || englishLabels.disrupted,
    normalServiceMessage: record?.normalServiceLabel || englishLabels.normalServiceMessage,
    stopInformation: record?.stopInformationLabel || englishLabels.stopInformation,
    travelUpdates: record?.travelUpdatesLabel || englishLabels.travelUpdates,
    latestUpdate: record?.latestUpdateLabel || englishLabels.latestUpdate,
    forecastLoadedAnnouncement: record?.forecastLoadedAnnouncement || englishLabels.forecastLoadedAnnouncement,
    servicesSuffix: record?.servicesSuffix || englishLabels.servicesSuffix,
  }
}

export function getLuasLineStatusLabel(operatingNormally: boolean, labels: LuasUiLabels): string {
  return operatingNormally ? labels.goodService : labels.disrupted
}

export function getLuasLineDisplayName(lineName: string, labels: LuasUiLabels): string {
  const withoutOperator = lineName.replace(/^Luas\s+/i, "")
  return `${withoutOperator.replace(/\s+Line$/i, " Line")} ${labels.servicesSuffix}`
}

export function getLuasServiceMessage(message: string, labels: LuasUiLabels): string {
  return /operating normally|normal service/i.test(message) ? labels.normalServiceMessage : message
}

export const fragments = graphql`
  fragment LuasUiLabelsFields on DatoCmsLuasUiLabel {
    lineLabel
    stopLabel
    selectLineLabel
    selectStopLabel
    findLabel
    loadingForecastsLabel
    loadingTravelUpdatesLabel
    stopsUnavailableLabel
    forecastUnavailableLabel
    travelUpdatesUnavailableLabel
    destinationLabel
    minutesLabel
    noTramsScheduledLabel
    normalServiceLabel
    disruptedLabel
    stopInformationLabel
    travelUpdatesLabel
    latestUpdateLabel
    forecastLoadedAnnouncement
    servicesSuffix
  }
`
